import { FlexCol } from '@/components/common/Flex';
import { FlexProps, Grid, InputProps } from '@chakra-ui/react';
import InputField from './InputField';
import { Text } from './Typography';
import useAutofocus from './useAutofocus';

export interface YearMonthOptions {
    allowPastDateSelection?: boolean;
    minYear?: number;
    maxYear?: number;
    yearOrder?: 'ASC' | 'DESC';
}

export interface YearMonthInputProps extends FlexProps {
    label?: string | React.ReactNode;
    yearMonth: string; // YYYYMM
    onChangeYearMonth: (yearMonth: string) => void;
    autoFocus?: boolean;
    autoFocusDelay?: number;
    yearTabIndex?: number;
    monthTabIndex?: number;
    inputProps?: InputProps;
    options?: YearMonthOptions;
}

const defaultMonthOptions = [
    { value: '01', label: 'Jan' },
    { value: '02', label: 'Feb' },
    { value: '03', label: 'Mar' },
    { value: '04', label: 'Apr' },
    { value: '05', label: 'May' },
    { value: '06', label: 'Jun' },
    { value: '07', label: 'Jul' },
    { value: '08', label: 'Aug' },
    { value: '09', label: 'Sep' },
    { value: '10', label: 'Oct' },
    { value: '11', label: 'Nov' },
    { value: '12', label: 'Dec' },
];

const getCurrentYearMonth = (): [string, string] => {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();
    return currentMonth <= 9
        ? [`0${currentMonth + 1}`, `${currentYear}`]
        : [`${currentMonth + 1}`, `${currentYear}`];
};

const getYearMonthFromString = (yearMonth: string): [string, string] => {
    if (!yearMonth) return ['', ''];
    if (yearMonth.length === 2) return [yearMonth, ''];
    if (yearMonth.length === 4) return ['', yearMonth];
    if (yearMonth.length === 6)
        return [yearMonth.slice(4), yearMonth.slice(0, 4)];
    return ['', ''];
};

export default function YearMonthInput({
    label,
    yearMonth,
    onChangeYearMonth,
    autoFocus,
    autoFocusDelay,
    yearTabIndex,
    monthTabIndex,
    inputProps,
    options,
    ...props
}: YearMonthInputProps) {
    const allowPastDateSelection = options?.allowPastDateSelection ?? false;
    const yearOrder = options?.yearOrder ?? 'ASC';

    const getMonthAndYearOptions = () => {
        const minYear = options?.minYear ?? new Date().getFullYear();
        const maxYear = options?.maxYear ?? new Date().getFullYear() + 100;

        const yearOptions = Array.from(
            { length: maxYear - minYear + 1 },
            (_, i) =>
                yearOrder === 'ASC' ? `${minYear + i}` : `${maxYear - i}`
        ).map((yearStr) => ({ value: yearStr, label: yearStr }));

        const monthlyOptions = defaultMonthOptions.map((o) => ({
            ...o,
            isDisabled:
                !allowPastDateSelection &&
                yearStr === currentYearStr &&
                o.value < currentMonthStr,
        }));

        return [monthlyOptions, yearOptions];
    };

    const firstInputRef = useAutofocus({ delay: autoFocusDelay });
    const [monthStr, yearStr] = getYearMonthFromString(yearMonth);
    const [currentMonthStr, currentYearStr] = getCurrentYearMonth();
    const [monthOptions, yearOptions] = getMonthAndYearOptions();

    const inputs = (
        <Grid gap={4} templateColumns={'1fr 80px'} alignItems="flex-end">
            <InputField
                name="month"
                label=""
                value={monthStr}
                onChange={(monthStr) =>
                    onChangeYearMonth(
                        !allowPastDateSelection &&
                            yearStr === currentYearStr &&
                            monthStr < currentMonthStr
                            ? yearStr + currentMonthStr
                            : yearStr + monthStr
                    )
                }
                type="select"
                options={monthOptions}
                inputProps={inputProps}
                ref={autoFocus ? firstInputRef : null}
                tabIndex={monthTabIndex}
                placeholder="Month"
            />
            <InputField
                name="year"
                label=""
                value={yearStr}
                onChange={(yearStr) =>
                    onChangeYearMonth(
                        !allowPastDateSelection &&
                            yearStr === currentYearStr &&
                            monthStr < currentMonthStr
                            ? yearStr + currentMonthStr
                            : yearStr + monthStr
                    )
                }
                type="select"
                options={yearOptions}
                inputProps={inputProps}
                tabIndex={yearTabIndex}
                placeholder="Year"
            />
        </Grid>
    );

    if (!label) return inputs;

    return (
        <FlexCol {...props}>
            <Text color="textMuted" fontSize="sm">
                {label}
            </Text>
            {inputs}
        </FlexCol>
    );
}
